import {
  amber,
  green,
  grey,
  lightBlue,
  pink,
  purple,
  teal,
  yellow,
} from "@mui/material/colors";
import { useFormikContext } from "formik";
import { useListedAlgo } from "../../lazertrader/contexts/listAlgosContext";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  SxProps,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useRef, useState } from "react";
import {
  Behavior,
  behaviorFromAlgoSpec,
  HIDDEN_TERMINATOR_BEHAVIORS,
  TerminatorBehavior,
} from "../algoSpec";
import { ROW_BUTTON_WIDTH, SMALL_INPUT_HEIGHT } from "./constants";

const getColor = (behavior: Behavior) => {
  switch (behavior) {
    case Behavior.LIMIT_ORDER:
      return amber[500];
    // case Behavior.NURSER:
    //   return blue[500];
    case Behavior.TERMINATOR:
      return purple[300];
    case Behavior.STEP_SHIMURAI:
      return green[500];
  }
};

// Skynet's colours for the same behaviours, so a trader moving between the two
// desks reads them the same way.
const getTerminatorColor = (behavior: TerminatorBehavior) => {
  switch (behavior) {
    case TerminatorBehavior.NURSER:
      return lightBlue[500];
    case TerminatorBehavior.TOP_NURSER:
      return teal[500];
    case TerminatorBehavior.WAPPER:
      return yellow[500];
    // Not a Skynet behaviour, so no colour to match.
    case TerminatorBehavior.LINEAR_CLOSER:
      return pink[300];
  }
};

/**
 * Editing a single algo: its type is fixed, so only its own behaviour stays
 * enabled. An unrecognised algo_spec leaves every button disabled.
 */
const useBehaviorDisabled = (content: Behavior, disabled?: boolean) => {
  const { selectedAlgos } = useListedAlgo();
  if (disabled || selectedAlgos.length !== 1) {
    return disabled ?? false;
  }
  return content !== behaviorFromAlgoSpec(selectedAlgos[0].algo_spec.name);
};

const switchSx = (background: string, width: number): SxProps => ({
  backgroundColor: background,
  color: "black",
  width,
  minWidth: width,
  height: SMALL_INPUT_HEIGHT,
  padding: "0 4px",
  textTransform: "none",
  whiteSpace: "nowrap",
  // As in `BuySellToggle`: `backgroundColor` outranks MUI's disabled
  // styling, so the dimming has to be stated.
  "&.Mui-disabled": {
    backgroundColor: background,
    color: "black",
    opacity: 0.45,
  },
});

export const BehaviorSwitch = (props: {
  content: Behavior;
  disabled?: boolean;
}) => {
  const color = getColor(props.content);
  const { values, setFieldValue } = useFormikContext<{ behavior: Behavior }>();
  const disabled = useBehaviorDisabled(props.content, props.disabled);

  const background = values.behavior === props.content ? color : grey[500];

  return (
    <Button
      disabled={disabled}
      variant="text"
      onClick={() => setFieldValue("behavior", props.content)}
      sx={switchSx(background, ROW_BUTTON_WIDTH)}
    >
      {props.content}
    </Button>
  );
};

/** Wider than the other behaviour buttons, to fit the two-line label. */
const TERMINATOR_BUTTON_WIDTH = 112;

/**
 * The Terminator button, which also picks the terminator's own behaviour
 * (`configuration.behaviors`) from a menu.
 *
 * The first click selects Terminator and opens the menu; once selected, a
 * click toggles it. The label then reads "TERMINATOR" over the chosen
 * behaviour — `DEFAULT_TERMINATOR_BEHAVIOR` on a new algo — or over "Pick one"
 * while none is picked, which only happens editing a terminator whose
 * behaviour this UI does not offer, and is the reason `SubmitButton` is
 * disabled.
 *
 * Changing the behaviour stays allowed when editing: it is configuration, not
 * the algo's type, so it is an ordinary update of a running terminator.
 *
 * While the menu would hold a single behaviour there is nothing to choose, so
 * the menu and its arrow are dropped and a click picks that one outright —
 * including over "Pick one". Editing a terminator on a hidden behaviour lists
 * two (its own plus the offered one), which brings the menu back.
 */
export const TerminatorSwitch = (props: { disabled?: boolean }) => {
  const { values, setFieldValue } = useFormikContext<{
    behavior?: Behavior;
    terminator_behavior?: TerminatorBehavior;
  }>();
  const disabled = useBehaviorDisabled(Behavior.TERMINATOR, props.disabled);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const selected = values.behavior === Behavior.TERMINATOR;
  const picked = values.terminator_behavior;
  const background = selected ? getColor(Behavior.TERMINATOR) : grey[500];

  // A hidden behaviour is listed only while it is the picked one, so editing a
  // terminator already on it still shows the tick.
  const choices = Object.values(TerminatorBehavior).filter(
    (behavior) =>
      !HIDDEN_TERMINATOR_BEHAVIORS.includes(behavior) || picked === behavior
  );
  const onlyChoice = choices.length === 1 ? choices[0] : undefined;

  const pick = (behavior: TerminatorBehavior) => {
    setFieldValue("terminator_behavior", behavior);
    setOpen(false);
  };

  return (
    <>
      <Button
        ref={anchorRef}
        disabled={disabled}
        variant="text"
        aria-haspopup={onlyChoice ? undefined : "menu"}
        aria-expanded={onlyChoice ? undefined : open}
        onClick={() => {
          if (!selected) {
            setFieldValue("behavior", Behavior.TERMINATOR);
          }
          if (onlyChoice) {
            if (picked !== onlyChoice) {
              setFieldValue("terminator_behavior", onlyChoice);
            }
            return;
          }
          setOpen((wasOpen) => !(selected && wasOpen));
        }}
        sx={{
          ...switchSx(background, TERMINATOR_BUTTON_WIDTH),
          justifyContent: "space-between",
          padding: "0 4px 0 10px",
        }}
      >
        {selected ? (
          <Box
            component="span"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              lineHeight: 1.15,
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 9,
                fontWeight: 500,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {Behavior.TERMINATOR}
            </Box>
            <Box
              component="span"
              sx={{ display: "flex", alignItems: "center", gap: "5px" }}
            >
              {picked === undefined ? (
                "Pick one"
              ) : (
                <>
                  <ColorDot
                    color={getTerminatorColor(picked)}
                    size={8}
                    outlined
                  />
                  {picked}
                </>
              )}
            </Box>
          </Box>
        ) : (
          <span>{Behavior.TERMINATOR}</span>
        )}
        {onlyChoice ? null : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
      </Button>
      <Menu
        anchorEl={anchorRef.current}
        open={open && !onlyChoice}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              width: 168,
              backgroundColor: (t) => t.palette.background.default,
            },
          },
          list: { dense: true, sx: { padding: 0.5 } },
        }}
      >
        <Typography
          component="li"
          sx={{
            padding: "6px 10px 4px",
            fontSize: 9,
            fontWeight: 500,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          Terminator behaviour
        </Typography>
        {choices.map((behavior) => (
          <MenuItem
            key={behavior}
            selected={picked === behavior}
            onClick={() => pick(behavior)}
            sx={{ gap: 1, borderRadius: 1 }}
          >
            <ColorDot color={getTerminatorColor(behavior)} size={10} />
            <Box component="span" sx={{ flex: 1 }}>
              {behavior}
            </Box>
            {picked === behavior ? <CheckIcon sx={{ fontSize: 14 }} /> : null}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const ColorDot = (props: {
  color: string;
  size: number;
  /** A dark edge, for a dot sitting on a coloured button. */
  outlined?: boolean;
}) => (
  <Box
    component="span"
    sx={{
      width: props.size,
      height: props.size,
      flexShrink: 0,
      borderRadius: "50%",
      backgroundColor: props.color,
      border: props.outlined ? "1px solid rgba(0, 0, 0, 0.45)" : "none",
    }}
  />
);
